import { call, put, select } from 'redux-saga/effects';
import { callWithAuth } from '../../sagaApiHelper';
import { getCampaignsApi } from '../../../api/apiLists/getCampaignsApi';
import { getCampaignAnalyticsApi } from '../../../api/apiLists/getCampaignAnalyticsApi';
import { createCampaignApi } from '../../../api/apiLists/createCampaignApi';
import { runCampaignApi } from '../../../api/apiLists/runCampaignApi';
import { updateCampaignApi } from '../../../api/apiLists/updateCampaignApi';
import { cancelCampaignApi } from '../../../api/apiLists/cancelCampaignApi';
import { uploadBuffer } from '../fileUploadSaga';
import { showAlert } from '../../alertSlice';
import {
  getTemplateHeaderMediaType,
  parseS3FileUrl,
  uploadCampaignTemplateMedia,
} from './campaignMediaHelper';
import {
  fetchCampaignsStart, fetchCampaignsSuccess, fetchCampaignsFailure,
  createCampaignScheduleStart, createCampaignScheduleSuccess, createCampaignScheduleFailure,
  updateCampaignScheduleStart, updateCampaignScheduleSuccess, updateCampaignScheduleFailure,
  cancelCampaignStart, cancelCampaignSuccess, cancelCampaignFailure,
  fetchCampaignsRequest,
  fetchCampaignAnalyticsStart, fetchCampaignAnalyticsSuccess, fetchCampaignAnalyticsFailure,
} from '../../slices/campaignsSlice';
import {
  buildCampaignAnalyticsDuration,
  parseCampaignAnalyticsDatapoints,
  aggregateCampaignDatapoints,
} from '../../../pages/whatsapp/utils/campaignAnalyticsUtils';
import { getOrFetchWabaId } from './utils';

/**
 * Fetch all campaigns for the current company
 */
export function* handleFetchCampaigns() {
  try {
    yield put(fetchCampaignsStart());

    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id || '');

    const req = {
      operation: 'getCampaigns',
      payload: { companyId }
    };

    const res = yield call(callWithAuth, getCampaignsApi, req);

    // Backend might return the array directly, or wrap it in an object
    if (res && !res.errMsg) {
      let campaigns = [];
      if (Array.isArray(res)) {
        campaigns = res;
      } else if (Array.isArray(res.data)) {
        campaigns = res.data;
      } else {
        campaigns = res.campaigns || res.list || [];
      }

      const stats = res.stats || {
        totalCampaigns: campaigns.length,
        messagesSent: '0',
        avgClickRate: '0%',
        totalSpent: '₹0'
      };
      yield put(fetchCampaignsSuccess({ list: campaigns, stats }));
    } else {
      yield put(fetchCampaignsFailure(res?.errMsg || 'Failed to fetch campaigns'));
    }
  } catch (error) {
    yield put(fetchCampaignsFailure(error?.message || 'Error fetching campaigns'));
  }
}

/**
 * Fetch analytics for a single campaign
 * Payload: { campaignId, duration?: { start, end } }
 */
export function* handleFetchCampaignAnalytics(action) {
  try {
    const { campaignId, duration } = action.payload || {};
    if (!campaignId) {
      yield put(fetchCampaignAnalyticsFailure('Campaign ID is required'));
      return;
    }

    yield put(fetchCampaignAnalyticsStart());

    const campaigns = yield select((state) => state.campaigns?.list || []);
    const campaign = campaigns.find(
      (c) => String(c.campaign_id) === String(campaignId) || String(c.campaignId) === String(campaignId)
    );
    const resolvedDuration = duration || buildCampaignAnalyticsDuration(campaign);

    const req = {
      operation: 'GET_CAMPAIGN_ANALYTICS',
      payload: {
        campaignId,
      },
    };

    const res = yield call(callWithAuth, getCampaignAnalyticsApi, req);

    if (res && !res.errMsg) {
      const datapoints = parseCampaignAnalyticsDatapoints(res);
      const totals = aggregateCampaignDatapoints(datapoints);
      yield put(fetchCampaignAnalyticsSuccess({
        campaignId,
        datapoints,
        totals,
        duration: resolvedDuration,
      }));
    } else {
      yield put(fetchCampaignAnalyticsFailure(res?.errMsg || 'Failed to fetch campaign analytics'));
    }
  } catch (error) {
    yield put(fetchCampaignAnalyticsFailure(error?.message || 'Error fetching campaign analytics'));
  }
}

/**
 * Create a scheduled campaign
 * Payload: { csv, fileName, variableMapping, templateId, executionMode, scheduledAt, campaignName }
 */
export function* handleCreateCampaignSchedule(action) {
  try {
    yield put(createCampaignScheduleStart());

    const {
      csv, fileName, variableMapping, templateId,
      executionMode = 'SCHEDULED', scheduledAt, campaignName
    } = action.payload || {};

    if (!csv) {
      yield put(showAlert({ message: 'No CSV content provided for campaign.', type: 'error' }));
      yield put(createCampaignScheduleFailure('No CSV content'));
      return;
    }

    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id || '');
    const createdBy = yield select(state => state.auth?.user?.email || state.auth?.user?.username || state.auth?.user?.id || '');
    const selectedPhone = yield select(state => state.whatsapp.selectedPhone || {});
    const templates = yield select(state => state.whatsapp.templates || []);
    const sendMediaFile = yield select(state => state.sendMessage?.sendMediaFile || null);
    const selectedTemplate = templates.find(t => String(t.id) === String(templateId)) || {};

    const wabaIdFromStorage = yield call(getOrFetchWabaId);
    const wabaId = wabaIdFromStorage || companyInfo?.wabaId || companyInfo?.whatsappBusinessAccountId || '';

    if (!wabaId) {
      yield put(showAlert({ message: 'WhatsApp Business Account (WABA) ID not found', type: 'error' }));
      yield put(createCampaignScheduleFailure('WABA ID not found'));
      return;
    }

    const mediaType = getTemplateHeaderMediaType(selectedTemplate);

    const mediaUpload = yield call(uploadCampaignTemplateMedia, {
      sendMediaFile,
      companyId,
      wabaId,
      displayPhoneNumberId: selectedPhone?.id,
    });

    if (!mediaUpload.ok) {
      yield put(showAlert({ message: mediaUpload.alertMessage, type: 'error' }));
      yield put(createCampaignScheduleFailure(mediaUpload.failureReason));
      return;
    }

    const mediaId = mediaUpload.mediaId;

    // Upload CSV
    const uploadFileName = fileName || `campaign-${Date.now()}.csv`;
    const uploadResult = yield call(uploadBuffer, {
      fileName: uploadFileName,
      contentType: 'text/csv',
      data: csv,
      customerId: companyId,
      operation: 'whatsapp-campaign',
    });

    if (!uploadResult || !uploadResult.fileURL) {
      yield put(showAlert({ message: 'Failed to upload CSV for campaign', type: 'error' }));
      yield put(createCampaignScheduleFailure('CSV upload failed'));
      return;
    }

    const { bucket, key } = parseS3FileUrl(uploadResult.fileURL);

    // Build campaign request
    const campaignReq = {
      operation: 'createCampaign',
      payload: {
        companyId,
        name: campaignName || selectedTemplate?.name || `Campaign ${Date.now()}`,
        category: selectedTemplate?.category || 'MARKETING',
        channel: 'WHATSAPP',
        channelAccountId: wabaId,
        execute: {
          mode: executionMode,
          scheduledAt: executionMode === 'SCHEDULED' ? scheduledAt : null
        },
        createdBy,
        audience: { source: 'S3', bucket, key },
        channelConfig: {
          WHATSAPP: {
            wabaId,
            sender: {
              senderNumber: selectedPhone?.number,
              displayPhoneNumberId: selectedPhone?.id
            },
            template: {
              id: selectedTemplate?.id,
              name: selectedTemplate?.name,
              language: selectedTemplate?.language
            },
            media: {
              type: mediaType,
              assetId: mediaId
            }
          }
        },
        variableMapping: variableMapping || {}
      }
    };

    const res = yield call(callWithAuth, createCampaignApi, campaignReq);

    if (res && (res.campaignId || res.status === 'SCHEDULED' || res.status === 'QUEUED' || String(res.status).toUpperCase() === 'SUCCESS' || String(res.status).toUpperCase() === 'PUBLISHED')) {
      if (executionMode === 'NOW' && res.campaignId) {
        const runCampaignReq = {
          operation: 'runCampaign',
          payload: {
            campaignId: res.campaignId,
            mode: executionMode,
          },
        };

        const runRes = yield call(callWithAuth, runCampaignApi, runCampaignReq);

        if (runRes && (runRes.status === 'OK' || String(runRes.status).toUpperCase() === 'SUCCESS')) {
          yield put(showAlert({ message: 'Campaign started successfully!', type: 'success' }));
        } else {
          yield put(showAlert({ message: runRes?.errMsg || 'Campaign created but failed to start', type: 'warning' }));
        }
      } else {
        yield put(showAlert({ message: 'Campaign scheduled successfully!', type: 'success' }));
      }

      yield put(createCampaignScheduleSuccess({
        campaignId: res.campaignId,
        status: res.status
      }));
    } else {
      const errMsg = res?.errMsg || res?.message || 'Failed to create campaign';
      yield put(showAlert({ message: errMsg, type: 'error' }));
      yield put(createCampaignScheduleFailure(errMsg));
    }
  } catch (error) {
    yield put(showAlert({ message: 'Error occurred while creating campaign', type: 'error' }));
    yield put(createCampaignScheduleFailure(error?.message || 'Campaign create failed'));
  }
}

/**
 * Update a scheduled campaign (reschedule)
 * Payload: { campaignId, scheduledAt }
 */
export function* handleUpdateCampaignSchedule(action) {
  try {
    yield put(updateCampaignScheduleStart());

    const { campaignId, scheduledAt } = action.payload || {};
    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id || '');

    const req = {
      operation: 'updateCampaign',
      payload: { campaignId, companyId, scheduledAt }
    };

    const res = yield call(callWithAuth, updateCampaignApi, req);

    if (res && (res.campaignId || res.status === 'SCHEDULED')) {
      yield put(showAlert({ message: 'Campaign rescheduled successfully!', type: 'success' }));
      yield put(updateCampaignScheduleSuccess({ campaignId: res.campaignId || campaignId, scheduledAt: res.scheduledAt || scheduledAt }));
      yield put(fetchCampaignsRequest());
    } else {
      const errMsg = res?.errMsg || 'Failed to update campaign';
      yield put(showAlert({ message: errMsg, type: 'error' }));
      yield put(updateCampaignScheduleFailure(errMsg));
    }
  } catch (error) {
    yield put(showAlert({ message: 'Error updating campaign schedule', type: 'error' }));
    yield put(updateCampaignScheduleFailure(error?.message || 'Update failed'));
  }
}

/**
 * Cancel a scheduled campaign
 * Payload: { campaignId }
 */
export function* handleCancelCampaign(action) {
  try {
    yield put(cancelCampaignStart());

    const { campaignId } = action.payload || {};
    const companyInfo = yield select(state => state.companyInfo?.info || {});
    const companyId = yield select(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id || '');

    const req = {
      operation: 'cancelCampaign',
      payload: { campaignId, companyId }
    };

    const res = yield call(callWithAuth, cancelCampaignApi, req);

    if (res && (res.status === 'CANCELLED' || String(res.status).toUpperCase() === 'SUCCESS')) {
      yield put(showAlert({ message: 'Campaign cancelled successfully', type: 'success' }));
      yield put(cancelCampaignSuccess({ campaignId: res.campaignId || campaignId }));
      yield put(fetchCampaignsRequest());
    } else {
      const errMsg = res?.errMsg || 'Failed to cancel campaign';
      yield put(showAlert({ message: errMsg, type: 'error' }));
      yield put(cancelCampaignFailure(errMsg));
    }
  } catch (error) {
    yield put(showAlert({ message: 'Error cancelling campaign', type: 'error' }));
    yield put(cancelCampaignFailure(error?.message || 'Cancel failed'));
  }
}
